import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import {
    faCheck,
    faChevronRight,
    faPencil,
    faWarning,
    faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/sharp-light-svg-icons";
import {
    faDrawPolygon,
    faEraser,
    faSpinner,
    faX,
} from "@fortawesome/sharp-regular-svg-icons";
import { Combobox, Portal } from "@headlessui/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { area } from "@turf/area";
import type { FeatureCollection } from "geojson";
import { useEffect, useMemo, useRef, useState } from "react";
import { type Control, useController } from "react-hook-form";
import type { FilterGroup, FilterItem } from "../../apiClient/generated";
import { useGeoDataApi } from "../../hooks";
import { UploadShapeButton } from "../../ui/Inputs";
import { DownloadDropdown } from "../MapV2/elements/DataControlsV2/AreasFilterControl";
import { MenuCategory } from "../MapV2/ui/Menu";

const LoadingIcon = () => (
    <div className="flex items-center justify-center">
        <FontAwesomeIcon icon={faArrowsRotate} className="w-4 animate-spin" />
    </div>
);

interface CheckboxItemProps {
    item: FilterItem;
    selected?: number[];
    onSelect: (selected: number[]) => void;
    // TODO: cleanup this
    small?: boolean;
}

const CheckboxItem = ({
    item,
    selected,
    onSelect,
    small,
}: CheckboxItemProps) => {
    const isChecked = selected?.some((i) => item.id === i);
    const handleClick = () => {
        if (isChecked) {
            onSelect((selected || []).filter((i) => i !== item.id));
        } else {
            onSelect([...(selected || []), item.id]);
        }
    };

    return (
        <label
            className={`flex items-center justify-start gap-2 ${small ? "h-7" : "h-8"}`}
            key={item.name}
        >
            <input
                type="checkbox"
                className="rounded text-ae-blue-550"
                checked={isChecked}
                onChange={handleClick}
            />
            <span className="min-w-0 flex-1 truncate" title={item.name}>
                {item.name}
            </span>
        </label>
    );
};

interface GeoFilterItemsProps {
    group: FilterGroup;
    items?: FilterItem[];
    selected?: number[];
    onSelect: (selection: number[]) => void;
    search?: string;
    // TODO: cleanup this
    small?: boolean;
}

export const GeoFilterItems = (props: GeoFilterItemsProps) => {
    const { selected, search, items } = props;
    const filterItems = useMemo(() => {
        if (!items) {
            return [];
        }

        return items
            .filter(
                (item) =>
                    // Don't show items not included in search
                    item.name
                        .toLowerCase()
                        .includes((search ?? "").toLowerCase()) ||
                    // Always show items that are selected
                    (selected || []).includes(item.id),
            )
            .toSorted((a, b) => {
                return a.name.localeCompare(b.name);
            });
    }, [items, selected, search]);

    return (
        <div className="overflow-y-scroll px-2 max-h-60">
            {filterItems.map((item) => (
                <CheckboxItem
                    key={item.id}
                    item={item}
                    onSelect={props.onSelect}
                    selected={props.selected}
                    small={props.small}
                />
            ))}
        </div>
    );
};

interface GeoFilterSelectDropdownV2Props {
    selected?: number[];
    onSelect: (selection: number[]) => void;
    customShape?: boolean;
}

export const GeoFilterSelectDropdownV2 = (
    props: GeoFilterSelectDropdownV2Props,
) => {
    const { selected } = props;
    const [search, setSearch] = useState("");
    const apiClient = useGeoDataApi();
    const [selectedGroup, setSelectedGroup] = useState<number | "all">("all");
    const inputRef = useRef<HTMLInputElement>();

    // Clear search when a selection is made
    useEffect(() => {
        setSearch("");
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [props.selected]);

    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "right-start",
        middleware: [
            offset({
                mainAxis: 10,
            }),
            shift(),
            flip({
                fallbackPlacements: ["bottom-start", "left-start"],
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    // Query filter groups
    const groupsQuery = useQuery({
        queryKey: ["geoFilterGroups"],
        queryFn: async () => {
            const response = await apiClient.geodataGroupsList({
                pageSize: 10,
            });
            return response.results;
        },
        staleTime: Infinity,
    });

    // Second set of queries to get details for each group
    const groupDetailsQueries = useQueries({
        queries:
            groupsQuery.data?.map((group) => ({
                queryKey: ["filterItem", group.id],
                queryFn: async () => {
                    const response = await apiClient.geodataItemsList({
                        pageSize: 500,
                        group: group.id,
                    });
                    return response.results;
                },
                staleTime: Infinity,
            })) || [],
    });

    const allItems = useMemo(() => {
        const items: FilterItem[] = [];
        for (const groupQuery of groupDetailsQueries) {
            if (groupQuery.data) {
                for (const i of groupQuery.data) {
                    items.push(i);
                }
            }
        }
        return items.toSorted((a, b) => a.name.localeCompare(b.name));
    }, [groupDetailsQueries]);

    const isLoading =
        groupsQuery.isLoading ||
        !groupsQuery.data ||
        groupDetailsQueries
            .map((q) => q.isLoading || q.data === undefined)
            .some((v) => v);

    return (
        <Combobox value={props.selected} onChange={props.onSelect} multiple>
            <div
                ref={refs.setReference}
                className="text-sm rounded flex justify-between border border-ae-gray-250 bg-white items-center px-2 focus-within:ring-2 ring-ae-blue-550"
            >
                <div className="flex flex-wrap gap-1 py-1 w-full">
                    {props.customShape && (
                        <button
                            type="button"
                            className="flex text-ae-blue-650 border border-ae-blue-80 bg-ae-blue-50 items-center rounded h-5 px-1 capitalize gap-1 whitespace-nowrap"
                            onClick={() => props.onSelect([])}
                        >
                            Custom shape
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="w-3 h-3"
                            />
                        </button>
                    )}
                    {props.selected?.map((id) => {
                        let itemName = "";
                        groupDetailsQueries.forEach((groupData) => {
                            const item = groupData.data?.find(
                                (i) => i.id === id,
                            );
                            if (item) {
                                itemName = item.name;
                                return;
                            }
                        });
                        return (
                            <button
                                key={id}
                                type="button"
                                title={itemName}
                                className="flex text-ae-blue-650 border border-ae-blue-80 bg-ae-blue-50 items-center rounded h-5 px-1 capitalize max-w-40 gap-1 whitespace-nowrap"
                                onClick={() => {
                                    props.onSelect(
                                        props.selected.filter((i) => i !== id),
                                    );
                                }}
                            >
                                <span className="truncate" title={itemName}>
                                    {itemName}
                                </span>
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    className="w-3 h-3"
                                />
                            </button>
                        );
                    })}
                    <Combobox.Input
                        ref={inputRef}
                        className="border-0 p-0 focus:ring-0 text-sm flex-grow"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={
                            (!props.selected || props.selected.length === 0) &&
                            !props.customShape
                                ? "All locations"
                                : undefined
                        }
                    />
                </div>
                <Combobox.Button className="px-2" type="button">
                    <FontAwesomeIcon
                        icon={faChevronRight}
                        className="w-3 h-3"
                    />
                </Combobox.Button>
            </div>
            <Portal>
                <Combobox.Options
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="bg-white rounded drop-shadow-lg z-50 w-96"
                >
                    {isLoading && <LoadingIcon />}
                    {!isLoading && (
                        <div className="flex text-sm p-1">
                            <div className="w-2/5">
                                <button
                                    type="button"
                                    className={`
                                    w-full px-2 py-1 flex items-center justify-between
                                    ${selectedGroup === "all" && "text-ae-blue-550 bg-ae-blue-40"}
                                `}
                                    onClick={() => setSelectedGroup("all")}
                                >
                                    All
                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className="w-2"
                                    />
                                </button>
                                {groupsQuery.data.map((group, idx) => {
                                    let selectedInGroup = 0;
                                    if (groupDetailsQueries[idx]?.data) {
                                        selectedInGroup =
                                            groupDetailsQueries[
                                                idx
                                            ].data.filter((item) =>
                                                (selected || []).includes(
                                                    item.id,
                                                ),
                                            ).length ?? 0;
                                    }
                                    return (
                                        <button
                                            key={group.id}
                                            type="button"
                                            className={`
                                            w-full px-2 py-1 flex items-center justify-between
                                            ${idx === selectedGroup && "text-ae-blue-550 bg-ae-blue-40"}
                                            ${selectedInGroup > 0 && "font-bold"}
                                        `}
                                            onClick={() =>
                                                setSelectedGroup(idx)
                                            }
                                        >
                                            {group.name}
                                            {selectedInGroup
                                                ? ` (${selectedInGroup})`
                                                : ""}
                                            <FontAwesomeIcon
                                                icon={faChevronRight}
                                                className="w-2"
                                            />
                                        </button>
                                    );
                                })}
                                {props.customShape && (
                                    <div className="bg-yellow-200 rounded px-2 py-1 text-xs flex items-center gap-2">
                                        <FontAwesomeIcon icon={faWarning} />
                                        Selecting a filter will remove the
                                        custom shape.
                                    </div>
                                )}
                            </div>
                            <div className="w-3/5 flex flex-col">
                                {selectedGroup !== "all" ? (
                                    groupDetailsQueries[selectedGroup] ? (
                                        <GeoFilterItems
                                            group={
                                                groupsQuery.data[selectedGroup]
                                            }
                                            items={
                                                groupDetailsQueries[
                                                    selectedGroup
                                                ].data
                                            }
                                            onSelect={props.onSelect}
                                            selected={props.selected}
                                            search={search}
                                            small
                                        />
                                    ) : (
                                        <LoadingIcon />
                                    )
                                ) : (
                                    <GeoFilterItems
                                        group={{ id: null, name: "All" }}
                                        items={allItems}
                                        onSelect={props.onSelect}
                                        selected={props.selected}
                                        search={search}
                                        small
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </Combobox.Options>
            </Portal>
        </Combobox>
    );
};

interface GeoFilterAreaProps {
    selectedPresets?: number[];
    setSelectedPresets: (selection: number[]) => void;
    loadingPresets?: boolean;
    allowDownload?: boolean;
    // combinedShape: for the download button (e.g. preset union or drawn shape).
    combinedShape?: FeatureCollection;
    // Custom shape: committed/applied shape.
    allowCustomShape?: boolean;
    customShape?: FeatureCollection;
    setCustomShape?: (shape?: FeatureCollection) => void;
    // Pending shape: uploaded or drawn shape awaiting confirmation.
    // Fully controlled by the parent — same flow for upload and drawing.
    pendingShape?: FeatureCollection;
    setPendingShape?: (shape?: FeatureCollection) => void;
    // Drawing (map-based views only).
    allowDrawing?: boolean;
    startDrawing?: () => void;
    // True only while the user is actively drawing (polygon not yet closed).
    isDrawing?: boolean;
}

export const GeoFilterArea = (props: GeoFilterAreaProps) => {
    const { pendingShape } = props;

    const hasFilter =
        (props.selectedPresets?.length ?? 0) > 0 ||
        (props.customShape?.features.length ?? 0) > 0;

    const areaSqMiles = useMemo(() => {
        if (pendingShape && pendingShape.features?.length > 0) {
            return area(pendingShape) * 3.861e-7;
        }
        return 0;
    }, [pendingShape]);

    const handleApply = () => {
        props.setCustomShape?.(pendingShape);
        props.setPendingShape?.(undefined);
    };

    const handleClear = () => {
        props.setCustomShape?.(undefined);
        props.setSelectedPresets([]);
        props.setPendingShape?.(undefined);
    };

    const handleCancel = () => {
        props.setPendingShape?.(undefined);
        props.setCustomShape?.(undefined);
    };

    return (
        <div className="bg-white p-1 rounded text-sm">
            <div className="flex w-full items-center justify-between mb-1">
                <MenuCategory>
                    Filter by area
                    {props.loadingPresets && (
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="h-4 mr-2 animate-spin"
                        />
                    )}
                </MenuCategory>
                <div className="flex items-center gap-1">
                    {(hasFilter || pendingShape) && !props.isDrawing && (
                        <>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                            >
                                Clear
                                <FontAwesomeIcon icon={faEraser} />
                            </button>
                            {props.allowDownload &&
                                (pendingShape || props.combinedShape) && (
                                    <DownloadDropdown
                                        drawnShape={
                                            pendingShape ?? props.combinedShape
                                        }
                                    />
                                )}
                        </>
                    )}
                    {props.allowCustomShape &&
                        !pendingShape &&
                        !props.isDrawing && (
                            <UploadShapeButton
                                setShape={(s) => props.setPendingShape?.(s)}
                            />
                        )}
                    {props.allowDrawing &&
                        !props.isDrawing &&
                        !pendingShape && (
                            <button
                                type="button"
                                onClick={() => props.startDrawing?.()}
                                className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                            >
                                Draw
                                <FontAwesomeIcon icon={faDrawPolygon} />
                            </button>
                        )}
                    {props.isDrawing && (
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            Cancel
                            <FontAwesomeIcon icon={faX} />
                        </button>
                    )}
                </div>
            </div>
            {props.isDrawing ? (
                <div className="flex w-full items-center gap-2 text-ae-slate-600 text-xs">
                    <div className="px-1 flex items-center gap-1 italic">
                        <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
                        Drawing...
                    </div>
                </div>
            ) : pendingShape ? (
                <div className="flex w-full items-center gap-2 text-ae-slate-600 text-xs">
                    <div className="w-full flex items-center justify-between">
                        <p>Area: {areaSqMiles.toFixed(2)} sq mi</p>
                        <button
                            type="button"
                            onClick={handleApply}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            Apply filter
                            <FontAwesomeIcon icon={faCheck} />
                        </button>
                    </div>
                </div>
            ) : (
                <GeoFilterSelectDropdownV2
                    selected={props.selectedPresets}
                    onSelect={(v) => {
                        props.setSelectedPresets(v);
                        if (
                            v.length === 0 &&
                            (props.customShape?.features.length ?? 0) > 0
                        ) {
                            props.setCustomShape?.(undefined);
                        }
                    }}
                    customShape={
                        (props.selectedPresets?.length ?? 0) === 0 &&
                        (props.customShape?.features.length ?? 0) > 0
                    }
                />
            )}
        </div>
    );
};

interface GeoFilterFormFieldProps {
    locationPresetName: string;
    locationWithinName: string;
    control: Control<any>;
}

export const GeoFilterFormField = ({
    locationPresetName,
    locationWithinName,
    control,
}: GeoFilterFormFieldProps) => {
    const { field: locationPreset } = useController({
        name: locationPresetName,
        control,
    });
    const { field: locationWithin } = useController({
        name: locationWithinName,
        control,
    });
    const [pendingShape, setPendingShape] = useState<FeatureCollection>();
    return (
        <div className="border rounded">
            <GeoFilterArea
                selectedPresets={locationPreset.value}
                setSelectedPresets={(v) => locationPreset.onChange(v)}
                allowCustomShape
                customShape={locationWithin.value}
                setCustomShape={(v) => {
                    locationWithin.onChange(v ?? null);
                    if (v) {
                        locationPreset.onChange(null);
                    }
                }}
                pendingShape={pendingShape}
                setPendingShape={setPendingShape}
            />
        </div>
    );
};
